import React, { useEffect, useMemo, useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import { Box, Grid, Stack, TextField, Typography, InputAdornment } from '@mui/material';

import { ArrowRight, Search } from 'lucide-react';

import Axios from '../../Assets/Axios/Axios';

import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import Reveal from '../../Components/Reveal';

import { COLORS } from '../../Constant/COLORS';

import { getCategoryIcon, hasRealImage } from '../../Helpers/categoryIcon';
import { toCapitalize } from '../../Helpers';

import { GetCategories } from '../../Server/Query';

const AllCategories = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    Axios(GetCategories)
      .then(({ data, errors }) => {
        if (!errors && data?.categories) setCategories(data.categories);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const filteredCategories = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return categories;
    return categories.filter(category =>
      [category.name, category.description]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(query))
    );
  }, [categories, search]);

  return (
    <>
      <Header isSellerOrCategoryPage />
      <Box maxWidth={{ xs: '90%', md: '80%' }} mx='auto' mt={{ xs: 4, md: 6 }} mb={10}>
        <Reveal>
          <Typography variant='overline' color={COLORS.teal} display='block'>
            Explore
          </Typography>
          <Typography variant='h3' mb={1}>
            All categories
          </Typography>
          <Typography color={COLORS.inkSoft} mb={3} maxWidth={560}>
            Browse every type of event professional on Evento and find the perfect
            match for your occasion.
          </Typography>
          <TextField
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder='Search categories'
            sx={{ width: '100%', maxWidth: 460, mb: { xs: 4, md: 6 } }}
            InputProps={{
              sx: { borderRadius: 999 },
              startAdornment: (
                <InputAdornment position='start'>
                  <Search size={18} color={COLORS.inkSoft} strokeWidth={1.8} />
                </InputAdornment>
              ),
            }}
          />
        </Reveal>

        <Grid container spacing={{ xs: 2, md: 3 }} alignItems='stretch'>
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Box
                    sx={{
                      height: 96,
                      borderRadius: 4,
                      border: `1px solid ${COLORS.hairline}`,
                      bgcolor: COLORS.surfaceAlt,
                    }}
                  />
                </Grid>
              ))
            : filteredCategories.map((category, index) => {
                const Icon = getCategoryIcon(category.name || category.slug);
                const showImage = hasRealImage(category.icon);
                return (
                  <Grid item xs={12} sm={6} md={4} key={category.id} sx={{ display: 'flex' }}>
                    <Reveal
                      delay={Math.min(index, 6) * 0.05}
                      style={{ width: '100%', display: 'flex' }}>
                      <Box
                        component={RouterLink}
                        to={`/category/${category.slug}`}
                        sx={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'center',
                          width: '100%',
                          p: 2.5,
                          borderRadius: 4,
                          border: `1px solid ${COLORS.hairline}`,
                          bgcolor: COLORS.surface,
                          textDecoration: 'none',
                          transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            borderColor: COLORS.teal,
                            boxShadow: '0 16px 34px rgba(14, 27, 31,0.10)',
                          },
                          '&:hover .cat-arrow': { transform: 'translateX(4px)' },
                          '&:hover .cat-badge': {
                            background: `linear-gradient(135deg, ${COLORS.teal}, ${COLORS.tealDark})`,
                            color: '#fff',
                          },
                        }}>
                        <Box
                          className='cat-badge'
                          sx={{
                            width: 58,
                            height: 58,
                            flexShrink: 0,
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${COLORS.tealSoft}, ${COLORS.surface})`,
                            border: `1px solid ${COLORS.hairline}`,
                            color: COLORS.teal,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background .25s ease, color .25s ease',
                          }}>
                          {showImage ? (
                            <img
                              src={category.icon}
                              alt=''
                              style={{ width: 30, height: 30, objectFit: 'contain' }}
                            />
                          ) : (
                            <Icon size={26} strokeWidth={1.6} />
                          )}
                        </Box>
                        <Box minWidth={0} flexGrow={1}>
                          <Typography variant='subtitle1' fontWeight={600} noWrap>
                            {toCapitalize(category.name)}
                          </Typography>
                          {category.description && (
                            <Typography
                              variant='caption'
                              color={COLORS.inkSoft}
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}>
                              {category.description}
                            </Typography>
                          )}
                        </Box>
                        <ArrowRight
                          className='cat-arrow'
                          size={18}
                          color={COLORS.teal}
                          style={{ flexShrink: 0, transition: 'transform .2s ease' }}
                        />
                      </Box>
                    </Reveal>
                  </Grid>
                );
              })}
        </Grid>

        {!isLoading && filteredCategories.length === 0 && (
          <Stack alignItems='center' py={8}>
            <Typography color={COLORS.inkSoft}>
              {search.trim()
                ? 'No categories match your search.'
                : 'No categories available yet.'}
            </Typography>
          </Stack>
        )}
      </Box>
      <Footer />
    </>
  );
};

export default AllCategories;
